import React, { useEffect, useRef, useState } from 'react';
import { streamChat } from '../../lib/streamChat.js';
import './Assistant.css';

// Page links are full navigations (see PageCurtain), which remount the app, so
// the conversation lives in sessionStorage to survive moving between pages.
const STORE_KEY = 'astrid-assistant';
// The server keeps the last 12 turns; sending more only grows the request.
const MAX_SENT = 12;

const SUGGESTIONS = [
  'What is an independent asset manager?',
  'Who holds my assets?',
  'Discretionary or advisory mandate?',
];

function loadStored() {
  try {
    const stored = JSON.parse(sessionStorage.getItem(STORE_KEY));
    if (Array.isArray(stored?.messages)) return stored;
  } catch {
    // Storage blocked or corrupt: start fresh.
  }
  return { open: false, messages: [] };
}

export const Assistant = () => {
  const initial = useRef(loadStored()).current;
  const [open, setOpen] = useState(initial.open);
  const [messages, setMessages] = useState(initial.messages);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const logRef = useRef(null);
  const inputRef = useRef(null);
  const launcherRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    try {
      sessionStorage.setItem(STORE_KEY, JSON.stringify({ open, messages }));
    } catch {
      // Storage blocked: the conversation just won't follow the visitor.
    }
  }, [open, messages]);

  useEffect(() => {
    const log = logRef.current;
    if (log) log.scrollTop = log.scrollHeight;
  }, [messages, error, open]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const close = () => {
    setOpen(false);
    launcherRef.current?.focus();
  };

  // On the window, not the panel: a panel restored open after a page change
  // does not hold focus, and Escape should still close it.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open]);

  const toggle = () => {
    if (open) return close();
    setOpen(true);
    // Wait for the panel to be displayed before it can take focus.
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const send = async (text) => {
    const question = text.trim();
    if (!question || busy) return;

    const history = [...messages, { role: 'user', content: question }];
    setMessages([...history, { role: 'assistant', content: '' }]);
    setDraft('');
    setError('');
    setBusy(true);

    const controller = new AbortController();
    abortRef.current = controller;
    try {
      await streamChat(
        history.slice(-MAX_SENT),
        (chunk) =>
          setMessages((current) => {
            const last = current[current.length - 1];
            return [...current.slice(0, -1), { ...last, content: last.content + chunk }];
          }),
        controller.signal
      );
    } catch (err) {
      if (err.name === 'AbortError') return;
      // Drop the empty reply bubble, keep whatever had already streamed.
      setMessages((current) =>
        current[current.length - 1]?.content ? current : current.slice(0, -1)
      );
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const onSubmit = (event) => {
    event.preventDefault();
    send(draft);
  };

  return (
    <div className="assistant" data-open={open ? 'true' : 'false'}>
      <div
        className="assistant-panel"
        id="assistant-panel"
        role="dialog"
        aria-label="Ask First"
        hidden={!open}
      >
        <div className="assistant-head">
          <img className="assistant-mark" src="/media/logo_background_remove.png" alt="" />
          <div className="assistant-head-text">
            <p className="assistant-title">Ask First</p>
            <p className="assistant-note">The ASTRID FIRST assistant</p>
          </div>
          <button type="button" className="assistant-close" aria-label="Close" onClick={close}>
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
              <path d="M6 6l12 12M18 6 6 18" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* Lenis captures wheel events page-wide; without this the conversation
            could not be scrolled with a mouse wheel or trackpad. */}
        <div className="assistant-log" ref={logRef} data-lenis-prevent aria-live="polite">
          <p className="assistant-msg" data-role="assistant">
            Good day. I'm First, the ASTRID FIRST assistant. Ask me about the firm, how our
            business model works, or how to reach the team.
          </p>

          {messages.map((message, index) => (
            <p key={index} className="assistant-msg" data-role={message.role}>
              {message.content || (
                <span className="assistant-typing" aria-label="Writing a reply">
                  <span />
                  <span />
                  <span />
                </span>
              )}
            </p>
          ))}

          {error && (
            <p className="assistant-error" role="alert">
              {error}
            </p>
          )}

          {messages.length === 0 && (
            <div className="assistant-suggestions">
              {SUGGESTIONS.map((suggestion) => (
                <button key={suggestion} type="button" onClick={() => send(suggestion)}>
                  {suggestion}
                </button>
              ))}
            </div>
          )}
        </div>

        <form className="assistant-form" onSubmit={onSubmit}>
          <label className="assistant-label" htmlFor="assistant-input">
            Your question
          </label>
          <input
            id="assistant-input"
            ref={inputRef}
            type="text"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder="Ask First a question"
            maxLength={1000}
            autoComplete="off"
            enterKeyHint="send"
          />
          <button
            type="submit"
            className="assistant-send"
            aria-label="Send"
            disabled={busy || !draft.trim()}
          >
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
              <path
                d="M12 19V5M5.5 11.5 12 5l6.5 6.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </form>
        <p className="assistant-disclaimer">
          First is an AI assistant. General information only, not financial advice.
        </p>
      </div>

      <button
        type="button"
        className="assistant-launcher"
        ref={launcherRef}
        aria-expanded={open}
        aria-controls="assistant-panel"
        onClick={toggle}
      >
        <img src="/media/logo_background_remove.png" alt="" />
        Ask First
      </button>
    </div>
  );
};
